import { useState, useEffect, useRef } from 'react';

export default function AdPlayer({ onAdComplete, userIsPremium }) {
  const [countdown, setCountdown] = useState(15); // 15 second ad by default
  const [adWatched, setAdWatched] = useState(false);
  const adRef = useRef(null);
  const adLoadedRef = useRef(false);

  useEffect(() => {
    // If user is premium, skip ad immediately
    if (userIsPremium) {
      onAdComplete();
      return;
    }

    // Load AdSense ad only once
    if (!adLoadedRef.current && adRef.current && window.adsbygoogle) {
      try {
        window.adsbygoogle.push({});
        adLoadedRef.current = true;
      } catch (error) {
        // Silently handle ad errors - they're expected during dev/refresh
      }
    }
  }, [userIsPremium, onAdComplete]);

  useEffect(() => {
    // Countdown timer in separate effect
    if (userIsPremium) return;

    if (countdown > 0 && !adWatched) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setAdWatched(true);
    }
  }, [countdown, adWatched, userIsPremium]);

  const handleSkipAd = () => {
    if (adWatched) {
      onAdComplete();
    }
  };

  // If premium user, don't show ad at all
  if (userIsPremium) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
      <div className="w-full max-w-4xl mx-auto px-4">
        {/* Ad Container */}
        <div className="bg-[#1c1c1e] rounded-3xl overflow-hidden shadow-2xl">
          {/* Ad Display Area */}
          <div className="aspect-video bg-gradient-to-br from-[#2c2c2e] to-[#1c1c1e] flex items-center justify-center relative overflow-hidden min-w-[300px]">
            {/* Google AdSense Video Ad */}
            <div className="w-full h-full flex items-center justify-center p-2 sm:p-4">
              <ins
                ref={adRef}
                className="adsbygoogle"
                style={{ display: 'block', minWidth: '250px', width: '100%', height: 'auto', maxWidth: '728px' }}
                data-ad-client="ca-pub-5068279696254934"
                data-ad-slot="8541001192"
                data-ad-format="auto"
                data-full-width-responsive="true"
              ></ins>
            </div>

            {/* Countdown Badge */}
            <div className="absolute top-4 right-4 px-4 py-2 bg-black/60 backdrop-blur-xl rounded-full border border-white/10 z-10">
              <span className="text-white font-medium">
                {countdown > 0 ? `${countdown}s` : 'Ad Complete'}
              </span>
            </div>
          </div>

          {/* Ad Controls */}
          <div className="p-6 bg-[#2c2c2e]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium mb-1">
                  {countdown > 0 ? 'Please wait...' : 'Ready to join!'}
                </p>
                <p className="text-white/60 text-sm">
                  {countdown > 0
                    ? `Your meeting will start in ${countdown} seconds`
                    : 'Click continue to join your meeting'
                  }
                </p>
              </div>
              <button
                onClick={handleSkipAd}
                disabled={countdown > 0}
                className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-200 ${
                  countdown > 0
                    ? 'bg-white/10 text-white/30 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#06c] to-[#0055d4] text-white hover:shadow-xl hover:scale-105'
                }`}
              >
                {countdown > 0 ? 'Please Wait' : 'Continue to Meeting'}
              </button>
            </div>
          </div>

          {/* Premium Upsell */}
          <div className="border-t border-white/10 p-6 bg-[#1c1c1e]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#06c] to-[#0055d4] rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-semibold">Skip ads forever</p>
                  <p className="text-white/60 text-sm">Just $5.50 AUD/month to support QuickMeet</p>
                </div>
              </div>
              <button
                onClick={() => window.location.href = '/dashboard?upgrade=true'}
                className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-xl font-medium transition-all duration-200"
              >
                Go Premium
              </button>
            </div>
          </div>
        </div>

        {/* Skip Info */}
        <div className="mt-6 text-center">
          <p className="text-white/50 text-sm">
            Ads help us keep QuickMeet free for everyone. Thank you for your support!
          </p>
        </div>
      </div>
    </div>
  );
}
